import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Dialog, DialogCloseButton, DialogFooter } from "@/components/dialog";
import { Modal } from "@/components/modal";

import type { SizeItem } from "./size-presets";

import { sameSize, SIZE_PRESETS, sizeIcon } from "./size-presets";

type EditSizeModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (sizes: SizeItem[]) => void;
  value: SizeItem[];
};

/** Parse a free-typed "1080x1080" / "1080 × 1920" into a normalized size. */
function parseCustomSize(raw: string): null | SizeItem {
  const match = raw.match(/(\d+)\s*[x×]\s*(\d+)/i);

  if (!match) return null;

  return { label: "Custom", size: `${match[1]}×${match[2]}px` };
}

export function EditSizeModal({
  isOpen,
  onOpenChange,
  onSave,
  value,
}: EditSizeModalProps) {
  const [selected, setSelected] = useState<SizeItem[]>(value);
  const [custom, setCustom] = useState("");

  // Reseed local selection whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelected(value);
      setCustom("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isSelected = (item: SizeItem) =>
    selected.some((current) => sameSize(current, item));

  const toggle = (item: SizeItem) => {
    setSelected((prev) =>
      prev.some((current) => sameSize(current, item))
        ? prev.filter((current) => !sameSize(current, item))
        : [...prev, item],
    );
  };

  const addCustom = () => {
    const parsed = parseCustomSize(custom);

    if (!parsed) return;
    if (!selected.some((current) => sameSize(current, parsed))) {
      setSelected((prev) => [...prev, parsed]);
    }
    setCustom("");
  };

  // Presets first, then any selected custom sizes that aren't presets.
  const cards: SizeItem[] = [
    ...SIZE_PRESETS,
    ...selected.filter(
      (item) => !SIZE_PRESETS.some((preset) => sameSize(preset, item)),
    ),
  ];

  return (
    <Modal
      classNames={{ modal: "w-[900px] max-w-[calc(100vw-48px)]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex flex-col p-6">
        <DialogCloseButton />

        <h2 className="text-xl font-semibold text-text-primary">Edit size</h2>
        <p className="mt-2 max-w-[560px] text-sm text-text-tertiary">
          Choose a preset size or enter a custom size deliverable in WxH format
          and press enter
        </p>

        {/* Custom size input */}
        <div className="mt-5 flex items-center gap-2 rounded-lg border border-border-primary bg-bg-primary px-3.5 py-3">
          <input
            className="flex-1 bg-transparent text-sm text-text-primary outline-hidden placeholder:text-text-placeholder"
            onChange={(event) => setCustom(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addCustom();
              }
            }}
            placeholder="Select a format"
            value={custom}
          />
          <ChevronDown className="size-5 text-fg-quaternary" />
        </div>

        {/* Preset + selected cards */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {cards.map((item) => {
            const Icon = sizeIcon(item.size);
            const active = isSelected(item);

            return (
              <button
                aria-pressed={active}
                className={twMerge(
                  "flex flex-col items-center gap-2 rounded-xl border border-border-secondary bg-bg-secondary px-4 py-5 text-center outline-hidden transition-colors hover:border-border-brand",
                  active && "border-border-brand bg-utility-brand-50",
                )}
                key={`${item.label}-${item.size}`}
                onClick={() => toggle(item)}
                type="button"
              >
                <Icon className="size-6 text-fg-brand-primary" />
                <span className="text-md font-semibold text-text-primary">
                  {item.label}
                </span>
                <span className="text-sm text-text-tertiary">{item.size}</span>
              </button>
            );
          })}
        </div>

        <DialogFooter className="mt-8 p-0">
          <Button
            className="flex-1"
            onPress={() => onOpenChange(false)}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onPress={() => {
              onSave(selected);
              onOpenChange(false);
            }}
            variant="primary"
          >
            Save
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
